/**
 * Tự cập nhật frontend cho user — user không phải làm gì, không thấy banner, không bấm nút.
 *
 * Vì sao cần: user không biết phải Ctrl+Shift+R sau mỗi lần deploy. Web app không thể thay code
 * đang chạy mà không tải lại trang, nên việc duy nhất làm được là TỰ CHỌN THỜI ĐIỂM reload sao cho
 * user không cảm nhận thấy, và tuyệt đối không cắt ngang việc họ đang làm.
 *
 * Bốn thời điểm an toàn, xét theo thứ tự ưu tiên:
 *   1. Tab đang ẩn            -> reload ngay, user quay lại đã là bản mới (chiếm đa số)
 *   2. Tab hiện nhưng user rảnh -> không gõ/click IDLE_MS -> reload
 *   3. User đổi trang         -> reload thẳng sang trang đích thay vì điều hướng SPA
 *   4. Đang có việc dở        -> HOÃN, chờ xong. An toàn vì deploy giữ lại file JS của 3 bản gần nhất
 *                                (xem deploy-frontend.sh), tab cũ không bao giờ chết.
 *
 * Điều kiện "đang có việc dở" do các trang tự khai báo qua registerBusy() bên dưới.
 */

import { useEffect, useRef } from 'react'
import { VERSION_APP } from 'consts'

const CHECK_INTERVAL_MS = 60000 // chu kỳ hỏi server có bản mới chưa
const IDLE_MS = 45000 // không gõ/click bao lâu thì coi là user đang rảnh
const IDLE_POLL_MS = 5000 // nhịp kiểm tra điều kiện rảnh khi đã biết có bản mới
const SCROLL_KEY = 'auto_update_scroll'

// ─── Sổ đăng ký "đang có việc dở" ───────────────────────────────────────────
// Trang nào đang nhập form/upload thì đăng ký một khoá; còn khoá nào thì không reload.
const busyKeys = new Set()

/**
 * Khai báo trang đang có việc dở (form nhập dở, upload đang chạy, modal đang mở).
 * @param {string} key - khoá duy nhất của việc đó
 * @returns {Function} gọi để bỏ đăng ký khi xong việc
 */
export const registerBusy = (key) => {
  busyKeys.add(key)
  return () => busyKeys.delete(key)
}

/** Có việc gì đang dở không — dùng để quyết định hoãn reload. */
export const isBusy = () => busyKeys.size > 0

// ─── Nạp lại trang, giữ nguyên chỗ user đang đứng ───────────────────────────
const reloadPreservingPlace = () => {
  try {
    // Lưu vị trí cuộn để sau khi tải lại đặt về đúng chỗ; URL tự giữ vì reload cùng địa chỉ.
    sessionStorage.setItem(
      SCROLL_KEY,
      JSON.stringify({ path: window.location.pathname + window.location.search, y: window.scrollY })
    )
  } catch (e) {
    // sessionStorage bị chặn thì thôi, vẫn reload bình thường
  }
  window.location.reload()
}

/** Khôi phục vị trí cuộn sau khi trang đã tải lại. */
const restoreScroll = () => {
  try {
    const raw = sessionStorage.getItem(SCROLL_KEY)
    if (!raw) return
    sessionStorage.removeItem(SCROLL_KEY)
    const { path, y } = JSON.parse(raw)
    if (path === window.location.pathname + window.location.search && y) {
      // đợi nội dung render xong mới cuộn
      setTimeout(() => window.scrollTo(0, y), 300)
    }
  } catch (e) {}
}

/**
 * Hook gắn một lần ở App. Tự kiểm tra bản mới và tự reload đúng lúc.
 */
const useAutoUpdate = () => {
  const hasNewVersion = useRef(false)
  const lastActivity = useRef(Date.now())

  useEffect(() => {
    restoreScroll()

    // Bản đang chạy trong tab này. 'dev' = chạy local chưa qua build -> không bật tự update.
    const runningVersion = VERSION_APP
    if (!runningVersion || runningVersion === 'dev') return

    let stopped = false

    const markActivity = () => {
      lastActivity.current = Date.now()
    }
    const events = ['mousedown', 'keydown', 'wheel', 'touchstart']
    events.forEach((e) => window.addEventListener(e, markActivity, { passive: true }))

    /** Hỏi server phiên bản mới nhất. cache:'no-store' để không bị trình duyệt trả lại bản cũ. */
    const checkVersion = async () => {
      if (stopped || hasNewVersion.current) return
      try {
        const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' })
        if (!res.ok) return
        const data = await res.json()
        if (data.version && data.version !== runningVersion) {
          hasNewVersion.current = true
          applyWhenSafe()
        }
      } catch (e) {
        // mất mạng / server đang deploy dở -> bỏ qua, chu kỳ sau thử lại
      }
    }

    /** Đã có bản mới: chờ tới thời điểm an toàn rồi reload. */
    const applyWhenSafe = () => {
      // #1 Tab đang ẩn -> reload ngay, user không thấy gì
      if (document.hidden && !isBusy()) {
        reloadPreservingPlace()
        return
      }

      // #2 Tab hiện: chờ user rảnh và không còn việc dở
      const timer = setInterval(() => {
        if (stopped) return clearInterval(timer)
        const idleEnough = Date.now() - lastActivity.current >= IDLE_MS
        if ((document.hidden || idleEnough) && !isBusy()) {
          clearInterval(timer)
          reloadPreservingPlace()
        }
      }, IDLE_POLL_MS)
    }

    // Kiểm tra ngay khi user quay lại tab — lúc dễ lệch phiên bản nhất (đi họp/ăn trưa về)
    const onVisibility = () => {
      if (!document.hidden) {
        markActivity()
        checkVersion()
      } else if (hasNewVersion.current && !isBusy()) {
        reloadPreservingPlace() // vừa chuyển sang tab khác -> tranh thủ update
      }
    }
    document.addEventListener('visibilitychange', onVisibility)

    const interval = setInterval(checkVersion, CHECK_INTERVAL_MS)
    checkVersion()

    return () => {
      stopped = true
      clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisibility)
      events.forEach((e) => window.removeEventListener(e, markActivity))
    }
  }, [])

  /** #3 Trang đổi route mà đang chờ bản mới -> nạp thẳng trang đích. */
  return {
    applyOnNavigate: (nextPath) => {
      if (hasNewVersion.current && !isBusy()) {
        window.location.href = nextPath
        return true // đã xử lý, không cần điều hướng kiểu SPA nữa
      }
      return false
    },
  }
}

export default useAutoUpdate
